/**
 * PranaFlow Academy LMS
 * General Utility Helpers
 */

/**
 * Extract 11-character Video ID from various YouTube URL formats
 */
export function getYouTubeVideoId(url) {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
}

/**
 * Generate high-quality YouTube thumbnail link for a video URL
 */
export function getYouTubeThumbnail(url) {
  const videoId = getYouTubeVideoId(url);
  return videoId 
    ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` 
    : '/assets/course_certificate.webp';
}

/**
 * Format date string into user-friendly format
 */
export function formatDate(dateString) {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Logs system activity to localStorage
 */
export function logSystemActivity(title, meta) {
  const logs = JSON.parse(localStorage.getItem('lms_activity_logs') || '[]');
  logs.unshift({
    title,
    meta,
    date: new Date().toLocaleTimeString() + " " + new Date().toLocaleDateString()
  });
  if (logs.length > 8) logs.pop();
  localStorage.setItem('lms_activity_logs', JSON.stringify(logs));
}

/**
 * Resolves static asset paths dynamically relative to Vite's BASE_URL
 */
export function getAssetUrl(path) {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  
  // Intercept the three protected courses so that they route to their protected endpoints even when requested as static assets
  if (path.includes('course_ttc200.webp')) {
    return getProtected200HourImageUrl();
  }
  if (path.includes('course_ttc300.webp')) {
    return getProtected300HourImageUrl();
  }
  if (path.includes('course_ttc500.webp')) {
    return getProtected500HourImageUrl();
  }
  if (path.includes('course_kids.webp')) {
    return getProtectedKidsImageUrl();
  }
  if (path.includes('course_nutrition.webp')) {
    return getProtectedNutritionImageUrl();
  }
  if (path.includes('course_face_yoga.webp')) {
    return getProtectedFaceYogaImageUrl();
  }

  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const base = import.meta.env.BASE_URL || '/';
  const separator = base.endsWith('/') ? '' : '/';
  return `${base}${separator}${cleanPath}`;
}

/**
 * Builds the correct absolute URL for a backend-uploaded file (e.g. profile pictures).
 *
 * API_BASE_URL ends with /api (e.g. http://localhost/my-lms/backend/api).
 * Uploaded files live under backend/uploads/, NOT backend/api/uploads/.
 * This function strips the trailing /api segment to get the backend root URL,
 * then appends the relative path returned by the upload APIs.
 *
 * @param {string|null} relativePath  - e.g. "uploads/profile_pictures/abc.webp"
 * @param {string|null} fallback      - URL to return when relativePath is falsy
 * @returns {string}
 */
export function getUploadUrl(relativePath, fallback = '') {
  if (!relativePath) return fallback;
  // If it is already an absolute URL, return as-is
  if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
    return relativePath;
  }
  // Derive backend root by removing the trailing /api segment from API_BASE_URL
  const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/my-lms/backend/api';
  const backendRoot = apiBase.endsWith('/api')
    ? apiBase.slice(0, -4)          // strip "/api"
    : apiBase.replace(/\/api$/, ''); // fallback regex strip
  const cleanPath = relativePath.startsWith('/') ? relativePath.slice(1) : relativePath;
  return `${backendRoot}/${cleanPath}`;
}

/**
 * Shared Reusable Course Image Resolver Function
 * Accepts a course object, batch object, course ID, or course title string.
 * Priority matching strategy:
 *   1. Explicit thumbnail/image property (if valid upload URL or HTTP URL or relative asset path)
 *   2. Course ID / Program ID matching (1 -> 200hr, 2 -> 300hr, 3 -> 500hr, 4 -> Face Yoga, 5 -> Kids, 6 -> Nutrition, 7 -> Therapy)
 *   3. Course Slug / Code matching (200, 300, 500, ttc200, ttc300, ttc500, face, kids, nutrition, therapy)
 *   4. Normalised Course Title matching
 *   5. Category / General fallback image
 *
 * @param {Object|string|number} item - Course object, batch object, or title/ID
 * @param {string} [fallbackThumbnail] - Optional explicit fallback thumbnail URL
 * @returns {string} Fully resolved image URL
 */
export function resolveCourseImageUrl(item, fallbackThumbnail = '') {
  if (!item && !fallbackThumbnail) {
    return getAssetUrl('assets/course-fallbacks/default-yoga-course.webp');
  }

  let courseObj = {};
  if (typeof item === 'object' && item !== null) {
    courseObj = item;
  } else if (typeof item === 'string' || typeof item === 'number') {
    courseObj = { title: String(item) };
  }

  // Check if item corresponds to 200H, 300H, or 500H Yoga Teacher Training
  const rawId = courseObj.course_id || courseObj.program_id || courseObj.id;
  const courseId = rawId ? String(rawId).trim() : '';
  const slug = (courseObj.slug || courseObj.course_slug || '').toLowerCase();
  const code = (courseObj.code || courseObj.course_code || '').toLowerCase();
  const title = (courseObj.title || courseObj.course_title || courseObj.name || '').toLowerCase();
  const explicitThumb = String(
    courseObj.img ||
    courseObj.thumbnail || 
    courseObj.course_thumbnail || 
    courseObj.image || 
    courseObj.course_image || 
    fallbackThumbnail || ''
  ).toLowerCase();

  if (
    courseId === '3' ||
    courseId === 'ttc500' ||
    title.includes('500 hour yoga teacher training') ||
    title.includes('500hr') ||
    explicitThumb.includes('500hr-yttc')
  ) {
    if (courseObj.img) return courseObj.img;
    return getAssetUrl('images/courses/500hr-yttc.webp');
  }

  if (
    courseId === '1' || 
    courseId === 'ttc200' ||
    slug.includes('200') || 
    code.includes('200') || 
    title.includes('200') || 
    explicitThumb.includes('ttc200') || 
    explicitThumb.includes('200') ||
    explicitThumb.includes('200hr-yttc')
  ) {
    if (courseObj.img) return courseObj.img;
    return getAssetUrl('images/courses/200hr-yttc.webp');
  }

  if (
    courseId === '2' || 
    courseId === 'ttc300' ||
    slug.includes('300') || 
    code.includes('300') || 
    title.includes('300') || 
    explicitThumb.includes('ttc300') || 
    explicitThumb.includes('300') ||
    explicitThumb.includes('300hr-yttc')
  ) {
    if (courseObj.img) return courseObj.img;
    return getAssetUrl('images/courses/300hr-yttc.webp');
  }

  if (
    courseId === '4' ||
    courseId === 'kids' ||
    slug.includes('kid') || 
    code.includes('kid') || 
    title.includes('kid') || 
    title.includes('child') ||
    title.includes('95') ||
    explicitThumb.includes('kids') ||
    explicitThumb.includes('course_kids') ||
    explicitThumb.includes('95-hour-kids-yoga-ttc')
  ) {
    if (courseObj.img) return courseObj.img;
    return getAssetUrl('images/courses/95-hour-kids-yoga-ttc.webp');
  }

  if (
    courseId === '5' ||
    courseId === 'nutrition' ||
    slug.includes('nutrition') || slug.includes('diet') ||
    code.includes('nutr') || code.includes('diet') ||
    title.includes('nutrition') || title.includes('diet') || title.includes('ayurveda') ||
    explicitThumb.includes('nutrition') ||
    explicitThumb.includes('course_nutrition') ||
    explicitThumb.includes('diet-and-nutrition')
  ) {
    if (courseObj.img) return courseObj.img;
    return getAssetUrl('images/courses/diet-and-nutrition.webp');
  }

  if (
    courseId === '6' ||
    courseId === 'face' ||
    courseId === 'face-yoga' ||
    slug.includes('face') ||
    code.includes('face') ||
    title.includes('face yoga') || title.includes('face-yoga') ||
    explicitThumb.includes('face_yoga') ||
    explicitThumb.includes('course_face') ||
    explicitThumb.includes('face-yoga-ttc')
  ) {
    if (courseObj.img) return courseObj.img;
    return getAssetUrl('images/courses/face-yoga-ttc.webp');
  }

  // 1. Check explicit thumbnail/image fields for other courses
  if (explicitThumb && explicitThumb.trim() !== '') {
    const thumb = explicitThumb.trim();
    if (thumb.startsWith('http://') || thumb.startsWith('https://')) {
      return thumb;
    }
    if (thumb.startsWith('uploads/') || thumb.startsWith('/uploads')) {
      return getUploadUrl(thumb);
    }
    if (thumb.startsWith('/assets/') || thumb.startsWith('assets/')) {
      return getAssetUrl(thumb);
    }
  }

  const idMap = {
    '4': 'assets/course_face_yoga.webp',
    '5': 'assets/course_kids.webp',
    '6': 'assets/course_nutrition.webp',
    '7': 'assets/course_therapy.webp',
  };

  if (courseId && idMap[courseId]) {
    return getAssetUrl(idMap[courseId]);
  }

  // 3. Priority 2 & 3: Slug & Code Matching for other courses
  if (slug.includes('face') || code.includes('face')) {
    return getAssetUrl('assets/course_face_yoga.webp');
  }
  if (slug.includes('kid') || code.includes('kid')) {
    return getAssetUrl('assets/course_kids.webp');
  }
  if (slug.includes('nutrition') || slug.includes('diet') || code.includes('nutr')) {
    return getAssetUrl('assets/course_nutrition.webp');
  }
  if (slug.includes('therapy') || slug.includes('anatomy') || code.includes('ther')) {
    return getAssetUrl('assets/course_therapy.webp');
  }

  // 4. Priority 4: Normalised Title Matching for other courses
  if (title.includes('face')) {
    return getAssetUrl('assets/course_face_yoga.webp');
  }
  if (title.includes('kid') || title.includes('child')) {
    return getAssetUrl('assets/course_kids.webp');
  }
  if (title.includes('nutrition') || title.includes('diet') || title.includes('ayurveda')) {
    return getAssetUrl('assets/course_nutrition.webp');
  }
  if (title.includes('therapy') || title.includes('anatomy')) {
    return getAssetUrl('assets/course_therapy.webp');
  }
  if (title.includes('meditation') || title.includes('dhyana')) {
    return getAssetUrl('assets/course-fallbacks/meditation-course.webp');
  }
  if (title.includes('pranayama') || title.includes('breath')) {
    return getAssetUrl('assets/course-fallbacks/pranayama-course.webp');
  }
  if (title.includes('philosophy') || title.includes('sutra') || title.includes('history')) {
    return getAssetUrl('assets/course-fallbacks/yoga-philosophy-course.webp');
  }
  if (title.includes('training') || title.includes('ttc') || title.includes('teacher')) {
    return getAssetUrl('assets/course-fallbacks/yoga-teacher-training.webp');
  }

  // Verified Fallback
  return getAssetUrl('assets/course-fallbacks/default-yoga-course.webp');
}

/**
 * Serves the protected Face Yoga image using the exact same flow as logo.php
 */
export function getProtectedFaceYogaImageUrl() {
  const apiBase = import.meta.env.VITE_API_BASE_URL || '/my-lms/backend/api';
  return `${apiBase}/course-image.php?course=face-yoga`;
}

/**
 * Serves the protected Nutrition Course image using the exact same flow as logo.php
 */
export function getProtectedNutritionImageUrl() {
  const apiBase = import.meta.env.VITE_API_BASE_URL || '/my-lms/backend/api';
  return `${apiBase}/course-image.php?course=nutrition`;
}

/**
 * Serves the protected Kids Yoga image using the exact same flow as logo.php
 */
export function getProtectedKidsImageUrl() {
  const apiBase = import.meta.env.VITE_API_BASE_URL || '/my-lms/backend/api';
  return `${apiBase}/course-image.php?course=kids`;
}

/**
 * Serves the protected 200 Hour image using the exact same flow as logo.php
 */
export function getProtected200HourImageUrl() {
  const apiBase = import.meta.env.VITE_API_BASE_URL || '/my-lms/backend/api';
  return `${apiBase}/course-image.php?course=200-hour`;
}

/**
 * Serves the protected 300 Hour image using the exact same flow as logo.php
 */
export function getProtected300HourImageUrl() {
  const apiBase = import.meta.env.VITE_API_BASE_URL || '/my-lms/backend/api';
  return `${apiBase}/course-image.php?course=300-hour`;
}

/**
 * Serves the protected 500 Hour image using the exact same flow as logo.php
 */
export function getProtected500HourImageUrl() {
  const apiBase = import.meta.env.VITE_API_BASE_URL || '/my-lms/backend/api';
  return `${apiBase}/course-image.php?course=500-hour`;
}

/**
 * Returns the consistent static course image URL based on course item or title.
 * Provided for backward compatibility across existing calls.
 */
export function getCourseImageUrl(courseTitle, fallbackThumbnail = '') {
  return resolveCourseImageUrl(courseTitle, fallbackThumbnail);
}


